import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaBriefcase,
  FaChartLine,
  FaClock,
  FaEnvelope,
  FaFileAlt,
  FaMapMarkerAlt,
  FaLaptopCode,
  FaUsers
} from "react-icons/fa";
import { loadCareerVacancies } from "./careerApi";
import "./Career.css";
import careerImage from "../uploads/wwww.jpg";

function Career() {
  const [vacancies, setVacancies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    loadCareerVacancies()
      .then((data) => {
        setVacancies(Array.isArray(data.vacancies) ? data.vacancies : []);
        setLoadError("");
      })
      .catch(() => {
        setLoadError("Vacancies are not available right now.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const benefits = [
    {
      icon: <FaUsers />,
      title: "Supportive Team",
      text: "Collaborate with focused project leads and friendly teammates."
    },
    {
      icon: <FaChartLine />,
      title: "Growth Path",
      text: "Learn practical workflows and build skills through real projects."
    },
    {
      icon: <FaLaptopCode />,
      title: "Digital Work",
      text: "Work across publishing, data processing, automation, and web solutions."
    }
  ];

  const getSummary = (text) => {
    const cleanText = String(text || "").trim();

    if (cleanText.length <= 135) {
      return cleanText;
    }

    return `${cleanText.slice(0, 135).trim()}...`;
  };

  return (
    <div className="career-wrapper">
      <section className="career-hero">
        <div className="career-hero-content">
          <span className="career-label">CAREER</span>
          <h1>
            Build Your Future With{" "}
            <span className="career-gradient-text">RDS Technology</span>
          </h1>
          <p>
            Join a growing technology team creating reliable digital solutions
            for publishing, business operations, and web platforms.
          </p>
          <a href="#openings" className="career-hero-btn">
            View Openings
          </a>
        </div>
      </section>

      <section className="career-intro">
        <div className="career-intro-image">
          <img src={careerImage} alt="RDS Technology career team" />
        </div>
        <div className="career-intro-text">
          <span>Why Join Us</span>
          <h2>Learn, contribute, and grow with meaningful project work.</h2>
          <p>
            At RDS Technology, we value responsibility, learning mindset, and
            consistent delivery. Whether you are beginning your career or
            growing your technical skills, you will work with teams that care
            about quality and practical outcomes.
          </p>
          <div className="career-highlights">
            <div>
              <strong>Training</strong>
              <small>Project-based learning</small>
            </div>
            <div>
              <strong>Teamwork</strong>
              <small>Clear coordination</small>
            </div>
            <div>
              <strong>Quality</strong>
              <small>Reliable delivery</small>
            </div>
          </div>
        </div>
      </section>

      <section className="career-benefits">
        {benefits.map((benefit) => (
          <div className="career-benefit-card" key={benefit.title}>
            <div className="career-benefit-icon">{benefit.icon}</div>
            <h3>{benefit.title}</h3>
            <p>{benefit.text}</p>
          </div>
        ))}
      </section>

      <section className="career-openings" id="openings">
        <div className="career-section-heading">
          <span>Open Roles</span>
          <h2>Current Job Vacancies</h2>
          <p>
            Explore available roles and send your profile to our team for the
            next step.
          </p>
        </div>

        {isLoading ? (
          <div className="career-empty-state">
            <FaBriefcase />
            <h3>Loading vacancies</h3>
            <p>Please wait while we check current openings.</p>
          </div>
        ) : loadError ? (
          <div className="career-empty-state">
            <FaBriefcase />
            <h3>Unable to load vacancies</h3>
            <p>{loadError}</p>
          </div>
        ) : vacancies.length > 0 ? (
          <div className="career-jobs-grid">
            {vacancies.map((job) => (
                <article className="career-job-card" key={job.id}>
                  <div className="career-job-top">
                    <FaBriefcase />
                    <span>{job.type}</span>
                  </div>
                  <h3>{job.title}</h3>
                  <div className="career-job-meta">
                    {job.department && <span>{job.department}</span>}
                    {job.location && (
                      <span>
                        <FaMapMarkerAlt /> {job.location}
                      </span>
                    )}
                    {job.experience && (
                      <span>
                        <FaClock /> {job.experience}
                      </span>
                    )}
                  </div>
                  <p>{getSummary(job.description)}</p>

                  <div className="career-card-footer">
                    <small>
                      Posted {job.postedDate}
                      {job.deadline ? ` | Apply before ${job.deadline}` : ""}
                    </small>
                    <Link
                      className="career-apply-link"
                      to={`/career/${encodeURIComponent(job.id)}`}
                    >
                      View Details
                    </Link>
                  </div>
                </article>
              ))}
          </div>
        ) : (
          <div className="career-empty-state">
            <FaBriefcase />
            <h3>No vacancies posted yet</h3>
            <p>
              When admin or owner posts a job vacancy, it will be listed here
              for candidates.
            </p>
          </div>
        )}
      </section>

      <section className="career-process">
        <div className="career-process-card">
          <FaFileAlt />
          <h3>Send Resume</h3>
          <p>Share your latest resume with the role name in the subject.</p>
        </div>
        <div className="career-process-card">
          <FaUsers />
          <h3>Interview</h3>
          <p>Meet our team and discuss your skills, goals, and availability.</p>
        </div>
        <div className="career-process-card">
          <FaEnvelope />
          <h3>Join Team</h3>
          <p>Get onboarding support and start contributing to live projects.</p>
        </div>
      </section>
    </div>
  );
}

export default Career;
